interface LogLevel {
    DEBUG: number;
    INFO: number;
    WARN: number;
    ERROR: number;
}

const LOG_LEVELS: LogLevel = {
    DEBUG: 0,
    INFO: 1,
    WARN: 2,
    ERROR: 3,
};

class Logger {
    private isProduction: boolean;
    private currentLogLevel: number;

    constructor() {
        this.isProduction = import.meta.env.PROD;
        this.currentLogLevel = this.isProduction ? LOG_LEVELS.ERROR : LOG_LEVELS.DEBUG;
    }

    private shouldLog(level: number): boolean {
        return level >= this.currentLogLevel;
    }

    private formatMessage(level: string, message: any, ...args: any[]): void {
        if (this.isProduction) {
            // In production, only show errors and send them to your error tracking service
            if (level === 'ERROR') {
                // You can integrate with Sentry here since you already have it configured
                if (window.Sentry) {
                    window.Sentry.captureException(new Error(message));
                }
                // Still allow error console logs in production for critical issues
                console.error(`[${level}]`, message, ...args);
            }
            return;
        }

        // In development, show all logs with styling
        const timestamp = new Date().toISOString();
        const styles = {
            DEBUG: 'color: #6B7280; font-weight: normal;',
            INFO: 'color: #3B82F6; font-weight: normal;',
            WARN: 'color: #F59E0B; font-weight: bold;',
            ERROR: 'color: #EF4444; font-weight: bold;',
        };

        console.log(
            `%c[${timestamp}] [${level}]`,
            styles[level as keyof typeof styles],
            message,
            ...args
        );
    }

    debug(message: any, ...args: any[]): void {
        if (this.shouldLog(LOG_LEVELS.DEBUG)) {
            this.formatMessage('DEBUG', message, ...args);
        }
    }

    info(message: any, ...args: any[]): void {
        if (this.shouldLog(LOG_LEVELS.INFO)) {
            this.formatMessage('INFO', message, ...args);
        }
    }

    warn(message: any, ...args: any[]): void {
        if (this.shouldLog(LOG_LEVELS.WARN)) {
            this.formatMessage('WARN', message, ...args);
        }
    }

    error(message: any, ...args: any[]): void {
        if (this.shouldLog(LOG_LEVELS.ERROR)) {
            this.formatMessage('ERROR', message, ...args);
        }
    }

    // Legacy console.log replacement
    log(message: any, ...args: any[]): void {
        this.info(message, ...args);
    }
}

// Export a singleton instance
export const logger = new Logger();

// Export for global usage
declare global {
    interface Window {
        logger: Logger;
        Sentry?: any;
    }
}

// Make it available globally in development
if (!import.meta.env.PROD) {
    window.logger = logger;
}

export default logger; 